import type {
  CreatePartnerReportRequest,
  DiscordGuildMember,
  PartnerActivityPage,
  PartnerDetail,
  PartnerDiscord,
  PartnerDiscordHealth,
  PartnerDiscordWriteResponse,
  PartnerMemberWriteResponse,
  PartnerPack,
  PartnerReportWriteResponse,
  PartnerServerPacks,
  PartnerSummary,
  PartnerTier,
  PartnerUpdatesPage,
  PartnerUpdatesParams,
  PartnerUpdatesSummary,
  PartnerActivity,
  ReportView,
  SyncOutcome,
  UpdatePartnerReportRequest,
} from '../types/partners';

/**
 * Partner Hub: partner teams, their Discord channel, update reports and
 * server packs.
 *
 * Argus is where the team is kept; Mr. Roboto keeps Discord matching it.
 * argus-backend saves every write first and only then pokes the bot, so a
 * write that carries a `sync` outcome is saved even when that outcome says
 * the bot could not be reached. The browser never talks to the bot itself.
 */
const API_BASE_URL = 'https://spy.playcdu.co/api/notes';

/** Reads and writes that do not wait on the bot. */
const DEFAULT_TIMEOUT_MS = 20_000;
/**
 * Writes that poke Mr. Roboto and wait for its answer. The bot has 25 seconds
 * to reconcile and Argus waits up to 30, so this leaves room for both.
 */
const SYNC_TIMEOUT_MS = 40_000;

const FALLBACK: Record<number, string> = {
  400: 'That was not accepted. Check the highlighted fields.',
  401: 'Your Argus session has expired. Reload the page to sign in again.',
  403: 'Ask an admin for PR access (argus_pr_panel).',
  404: 'That no longer exists. It may have been removed by someone else.',
  409: 'That clashes with something already saved. Reload the page and try again.',
  413: 'That is too large to send.',
  429: 'Too many requests at once. Wait a moment and try again.',
  502: 'Mr. Roboto gave an answer Argus could not use.',
  503: 'Mr. Roboto cannot be reached right now.',
  504: 'Mr. Roboto took too long to answer.',
};

export class PartnersApiError extends Error {
  readonly status: number;
  /** The field the backend blamed, when it named one. */
  readonly field: string | null;
  /** On a 409 for a channel link: the channel Argus already holds. */
  readonly channelId: string | null;

  constructor(status: number, message: string, field: string | null = null, channelId: string | null = null) {
    super(message);
    this.name = 'PartnersApiError';
    this.status = status;
    this.field = field;
    this.channelId = channelId;
  }
}

async function readError(response: Response): Promise<PartnersApiError> {
  const fallback = FALLBACK[response.status] ?? `Request failed (${response.status}).`;
  try {
    const body = (await response.json()) as { error?: unknown; field?: unknown; channel_id?: unknown };
    const message = typeof body.error === 'string' && body.error.trim() ? body.error : fallback;
    const field = typeof body.field === 'string' && body.field ? body.field : null;
    const channelId = typeof body.channel_id === 'string' && body.channel_id ? body.channel_id : null;
    return new PartnersApiError(response.status, message, field, channelId);
  } catch {
    return new PartnersApiError(response.status, fallback);
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  // A caller's own signal (a search superseded by the next keystroke) aborts too.
  const external = init.signal ?? null;
  const onExternalAbort = () => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort);
  }
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
    if (!response.ok) throw await readError(response);
    // Access answers an expired session with a redirect to its login page,
    // which arrives here as a 200 full of HTML rather than as an error.
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) {
      throw new PartnersApiError(401, FALLBACK[401]);
    }
    return (await response.json()) as T;
  } catch (error) {
    if (external?.aborted && !timedOut) throw new DOMException('The request was superseded.', 'AbortError');
    if (timedOut) {
      throw new PartnersApiError(
        504,
        timeoutMs >= SYNC_TIMEOUT_MS
          ? 'Mr. Roboto took too long to answer. Anything you changed is saved and will apply on its next pass.'
          : 'Argus took too long to answer. Try again in a moment.',
      );
    }
    if (error instanceof PartnersApiError) throw error;
    throw new PartnersApiError(503, 'Argus could not be reached. Check your connection and try again.');
  } finally {
    clearTimeout(timer);
    external?.removeEventListener('abort', onExternalAbort);
  }
}

const json = (body: unknown, method: string): RequestInit => ({ method, body: JSON.stringify(body) });
const enc = encodeURIComponent;

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.append(key, String(value));
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

const partner = (creatorId: string) => `/partners/${enc(creatorId)}`;

class PartnersApiService {
  // ── Partners ─────────────────────────────────────────────────────────────

  async list(): Promise<PartnerSummary[]> {
    const { partners } = await request<{ partners: PartnerSummary[] }>('/partners');
    return partners;
  }

  get(creatorId: string): Promise<PartnerDetail> {
    return request(partner(creatorId));
  }

  activity(creatorId: string, params: { before?: number | null; limit?: number } = {}): Promise<PartnerActivityPage> {
    return request(`${partner(creatorId)}/activity${query({ before: params.before, limit: params.limit })}`);
  }

  /** Answers 503 until Tesseract carries partner context on its file transfers. */
  serverPacks(creatorId: string): Promise<PartnerServerPacks> {
    return request(`${partner(creatorId)}/server-packs`);
  }

  // ── Discord settings ─────────────────────────────────────────────────────

  updateDiscord(
    creatorId: string,
    patch: Partial<Pick<PartnerDiscord, 'managed' | 'early_access' | 'strict_access'>>,
  ): Promise<PartnerDiscordWriteResponse> {
    return request(`${partner(creatorId)}/discord`, json(patch, 'PUT'), SYNC_TIMEOUT_MS);
  }

  /** Adopt a channel that already exists. 409 when another partner holds it. */
  linkChannel(creatorId: string, channelId: string): Promise<PartnerDiscordWriteResponse> {
    return request(`${partner(creatorId)}/discord/link`, json({ channel_id: channelId }, 'POST'), SYNC_TIMEOUT_MS);
  }

  /** Forget the channel. Without `recreate`, management is switched off too. */
  unlinkChannel(creatorId: string, recreate: boolean): Promise<PartnerDiscordWriteResponse> {
    return request(`${partner(creatorId)}/discord/unlink`, json({ recreate }, 'POST'), SYNC_TIMEOUT_MS);
  }

  /** `dry_run` previews without touching Discord; `force` (PR admin) applies held removals. */
  async sync(creatorId: string, options: { dry_run?: boolean; force?: boolean } = {}): Promise<SyncOutcome> {
    const { sync } = await request<{ sync: SyncOutcome }>(`${partner(creatorId)}/discord/sync`, json(options, 'POST'), SYNC_TIMEOUT_MS);
    return sync;
  }

  // ── Roster ───────────────────────────────────────────────────────────────

  addMember(creatorId: string, body: { discord_id: string; tier: PartnerTier; note?: string | null }): Promise<PartnerMemberWriteResponse> {
    return request(`${partner(creatorId)}/members`, json(body, 'POST'), SYNC_TIMEOUT_MS);
  }

  updateMember(creatorId: string, memberId: string, patch: { tier?: PartnerTier; note?: string | null }): Promise<PartnerMemberWriteResponse> {
    return request(`${partner(creatorId)}/members/${enc(memberId)}`, json(patch, 'PATCH'), SYNC_TIMEOUT_MS);
  }

  /** Leaves a removed row behind: that row is what makes the bot take access away. */
  removeMember(creatorId: string, memberId: string): Promise<PartnerMemberWriteResponse> {
    return request(`${partner(creatorId)}/members/${enc(memberId)}`, { method: 'DELETE' }, SYNC_TIMEOUT_MS);
  }

  /** Take channel access from someone who is not on the team. */
  revokeAccess(creatorId: string, discordId: string): Promise<PartnerMemberWriteResponse> {
    return request(`${partner(creatorId)}/access/revoke`, json({ discord_id: discordId }, 'POST'), SYNC_TIMEOUT_MS);
  }

  /** Delete a removed row for good. 409 for an active member. */
  async forgetMember(creatorId: string, memberId: string): Promise<void> {
    await request<{ ok: true }>(`${partner(creatorId)}/members/${enc(memberId)}/forget`, { method: 'DELETE' });
  }

  // ── Packs ────────────────────────────────────────────────────────────────

  async setPackApogee(modpackId: string, apogeeId: number | null): Promise<PartnerPack> {
    const { pack } = await request<{ pack: PartnerPack }>(`/partners/modpacks/${enc(modpackId)}/apogee`, json({ apogee_id: apogeeId }, 'PUT'));
    return pack;
  }

  // ── Update reports ───────────────────────────────────────────────────────

  updates(params: PartnerUpdatesParams = {}): Promise<PartnerUpdatesPage> {
    return request(`/partners/updates${query({ ...params })}`);
  }

  updatesSummary(): Promise<PartnerUpdatesSummary> {
    return request('/partners/updates/summary');
  }

  report(id: string): Promise<{ report: ReportView; events: PartnerActivity[] }> {
    return request(`/partners/updates/${enc(id)}`);
  }

  createReport(body: CreatePartnerReportRequest): Promise<PartnerReportWriteResponse> {
    return request('/partners/updates', json(body, 'POST'), SYNC_TIMEOUT_MS);
  }

  updateReport(id: string, patch: UpdatePartnerReportRequest): Promise<PartnerReportWriteResponse> {
    return request(`/partners/updates/${enc(id)}`, json(patch, 'PATCH'), SYNC_TIMEOUT_MS);
  }

  async linkCurseForge(id: string, file: { cf_file_id: number; display_name: string }): Promise<ReportView> {
    const { report } = await request<{ report: ReportView }>(`/partners/updates/${enc(id)}/link-curseforge`, json(file, 'POST'));
    return report;
  }

  // ── Mr. Roboto, through Argus ────────────────────────────────────────────

  discordHealth(): Promise<PartnerDiscordHealth> {
    return request('/partners/discord/health');
  }

  /** Search the guild (bots excluded, at most 10), or look up to 50 ids. */
  async discordMembers(params: { q: string } | { ids: string[] }, signal?: AbortSignal): Promise<DiscordGuildMember[]> {
    const qs = 'q' in params ? query({ q: params.q }) : query({ ids: params.ids.slice(0, 50).join(',') });
    const { members } = await request<{ members: DiscordGuildMember[] }>(`/partners/discord/members${qs}`, signal ? { signal } : {});
    return members;
  }
}

export const partnersApi = new PartnersApiService();
